import { Controller } from "@hotwired/stimulus"
import { post } from "@rails/request.js"

// Mirrors app/helpers/area_formatter.rb and shared/lib/format-area.ts.
const SQ_FEET_PER_SQ_METER = 10.7639104167097
const TAG_COMMIT_KEYS = ["Enter", "Tab", ","]

// Connects to data-controller="contacts--company-creation"
//
// Drives the "New Site" drawer and its three sub-drawers (CONEK-6530), the
// Hotwire counterpart of React's CompanyCreationDrawer + CompanyGroupDrawer +
// SocialMediaDrawer + ShippingAddressDrawer.
//
// Two different kinds of sub-record, deliberately handled differently — the same
// split React makes:
//
//   * Social media and shipping addresses ride along in the company's own
//     payload. Saving one appends hidden inputs to the company form, so a single
//     request still creates the company and all of its children.
//   * An Ultimate Parent Group is created immediately, because the company's
//     `group_id` is required — ContactCompanies::Create refuses a company
//     without one, so the group has to exist before the company can name it.
//
// Submitting the company form itself is left to Turbo (turbo:submit-end →
// created()). On the Validate tab the endpoint links the site and redirects, so
// closing the drawer is all there is to do. On the contact Edit form
// (CONEK-6835) the endpoint answers 204 + X-Created-Site-Id/-Name headers and
// `selectInto` names the entity selector to commit the new site into; the page
// never navigates, so once the drawer has closed it is put back to its
// server-rendered state — otherwise the next "+ Create" would open on the
// previous site's name, group, owner and addresses.
//
// The controller is mounted on a wrapper around the drawer AND its sub-drawers:
// the sub-drawers cannot live inside the company <form> (nested forms are
// invalid HTML and their fields would submit with the company), so a controller
// mounted on the form itself could not reach them.
export default class extends Controller {
  static targets = [
    "hiddenInputs",
    "socialMediaFields",
    "socialMediaList",
    "socialMediaEmpty",
    "addressFields",
    "addressList",
    "addressEmpty",
    "groupFields",
    "groupSelector",
    "groupSegment",
    "groupCategory",
    "groupSubcategory",
    "groupSubcategoryField",
    "groupFinancialFields",
    "groupError",
    "areaField",
    "errors",
  ]

  static values = {
    industryParents: Object,
    industrySegments: Object,
    nonEndUserSegment: String,
    groupUrl: String,
    groupStatus: String,
    formId: String,
    areaLabels: Object,
    areaImperialLabel: String,
    branchCurrencies: Object,
    selectInto: String,
  }

  connect() {
    // Edit mode puts this markup back after a successful create (see
    // _resetDrawerAfterClose). Captured before anything else runs: Stimulus
    // connects a wrapper before its descendants, so no child controller (Tom
    // Select, the entity selectors) has touched the DOM yet.
    if (this._selectMode) {
      this._pristineHtml = this.element.innerHTML
      // Turbo Drive snapshots the page on leave and restores it on Back with the
      // enhanced DOM in it; a controller reconnecting on that snapshot would
      // capture Tom Select's markup as "pristine" and the reset would then
      // double-enhance. Put the server markup back before the snapshot is taken,
      // so the cached page — and every later capture — is the clean render.
      this._onBeforeCache = () => this._restorePristine()
      document.addEventListener("turbo:before-cache", this._onBeforeCache)
    }
    this._socialMediaCount = 0
    this._addressCount = 0
    // The group pair starts collapsed to the empty selection, matching React,
    // which renders no subcategory until a category with children is chosen.
    this.groupCategoryChanged()
    // A no-op until the group sub-drawer has a segment selected.
    this.groupSegmentChanged()
    this._syncFirstAddressDefault()
  }

  disconnect() {
    if (this._onBeforeCache) {
      document.removeEventListener("turbo:before-cache", this._onBeforeCache)
      this._onBeforeCache = null
    }
    this._disarmDrawerClose()
  }

  // --- Site area unit -----------------------------------------------------

  // The area field is labelled in the subsidiary's own unit (m² or ft²), so
  // switching subsidiary relabels it AND reconverts what is already typed —
  // relabelling alone would silently reinterpret 500 m² as 500 ft². The stored
  // value stays square metres; the controller converts the entry on submit.
  branchChanged(event) {
    this._resetCurrencies(
      this.branchCurrenciesValue?.[event.target.value] ?? [],
    )
    if (!this.hasAreaFieldTarget) return

    const label = this.areaLabelsValue[event.target.value]
    if (!label) return

    const labelEl = this.areaFieldTarget.querySelector("label")
    const previousLabel = labelEl?.textContent?.trim()
    if (labelEl) labelEl.textContent = label

    const input = this.areaFieldTarget.querySelector("input")
    if (!input?.value || previousLabel === label) return

    const typed = Number(input.value)
    if (Number.isNaN(typed)) return

    const wasImperial = previousLabel === this.areaImperialLabelValue
    const isImperial = label === this.areaImperialLabelValue
    if (wasImperial === isImperial) return

    const squareMeters = wasImperial ? typed / SQ_FEET_PER_SQ_METER : typed
    const next = isImperial ? squareMeters * SQ_FEET_PER_SQ_METER : squareMeters
    input.value = `${Math.round(next * 100) / 100}`
  }

  // --- Format validation --------------------------------------------------

  // The tag input's create_filter already refuses an invalid tag; this only
  // says why, as React's per-tag message does.
  checkTag(event) {
    const container = event.currentTarget
    if (!TAG_COMMIT_KEYS.includes(event.key)) {
      this._showFormatError(container, false)
      return
    }
    const value = event.target.value?.trim() ?? ""
    const valid = !value || new RegExp(event.params.pattern).test(value)
    this._showFormatError(container, !valid)
  }

  checkFax(event) {
    const value = event.target.value.trim()
    const valid = !value || new RegExp(event.params.pattern).test(value)
    this._showFormatError(event.currentTarget, !valid)
  }

  guardFormat(event) {
    const error = Array.from(
      this.element.querySelectorAll("[data-format-error][data-blocks-submit]"),
    ).find((el) => !el.classList.contains("hidden"))
    if (!error) return
    event.preventDefault()
    event.stopImmediatePropagation()
    error.scrollIntoView({ block: "center" })
  }

  _showFormatError(container, show) {
    container
      .querySelector("[data-format-error]")
      ?.classList.toggle("hidden", !show)
  }

  // --- Social media -------------------------------------------------------

  saveSocialMedia() {
    const values = this._readFields(this.socialMediaFieldsTarget)
    if (!values.platform_id || !values.url) return

    const index = this._socialMediaCount++
    const rowKey = `social_media-${index}`
    this._appendHidden(
      "social_media",
      index,
      {
        platform_id: values.platform_id,
        url: values.url,
        username: values.username,
      },
      rowKey,
    )
    this._appendSummary(
      this.socialMediaListTarget,
      this.socialMediaEmptyTarget,
      [values.username, values.url].filter(Boolean).join(" · "),
      rowKey,
    )
    this._clearFields(this.socialMediaFieldsTarget)
    this._closeDrawerContaining(this.socialMediaFieldsTarget)
  }

  // --- Shipping addresses -------------------------------------------------

  // The same five the React drawer validates, and the same five
  // Contacts::Companies::AddressDrawerComponent::REQUIRED marks — a row without
  // a name aborts the company create outright, and one without street or city is
  // silently dropped by ContactCompanies::Create.
  static ADDRESS_REQUIRED = [
    "name",
    "address",
    "city",
    "postal_code",
    "country_code",
  ]

  saveAddress() {
    const values = this._readFields(this.addressFieldsTarget)
    if (this.constructor.ADDRESS_REQUIRED.some((key) => !values[key])) return

    const index = this._addressCount++
    const rowKey = `address-${index}`
    this._appendHidden("sites][0][shipping_addresses", index, values, rowKey)
    this._appendSummary(
      this.addressListTarget,
      this.addressEmptyTarget,
      [
        values.address,
        values.street_number,
        values.postal_code,
        values.city,
        values.country_code,
      ]
        .filter(Boolean)
        .join(", "),
      rowKey,
    )
    this._clearFields(this.addressFieldsTarget)
    this._syncFirstAddressDefault()
    this._closeDrawerContaining(this.addressFieldsTarget)
  }

  // The first address is always the default one, as in React.
  _syncFirstAddressDefault() {
    if (!this.hasAddressFieldsTarget || !this.hasAddressListTarget) return
    const node = this.addressFieldsTarget.querySelector(
      '[data-contacts--company-creation-field="is_default"]',
    )
    const checkbox = node && this._control(node)
    if (!checkbox) return
    const first = this.addressListTarget.children.length === 0
    checkbox.disabled = first
    if (first) checkbox.checked = true
  }

  _resetCurrencies(codes) {
    const select = this.element.querySelector(
      'select[name="contact_company[currencies][]"]',
    )
    const tomSelect = select?.tomselect
    if (!tomSelect) return
    tomSelect.clear(true)
    codes.forEach((code) => tomSelect.addItem(code, true))
  }

  // --- Ultimate Parent Group ----------------------------------------------

  async saveGroup(event) {
    const button = event?.currentTarget
    const values = this._readFields(this.groupFieldsTarget)
    if (!values.name) return

    const subIndustry = this.hasGroupSubcategoryTarget
      ? this._selectValue(this.groupSubcategoryTarget)
      : null
    const industry = this.hasGroupCategoryTarget
      ? this._selectValue(this.groupCategoryTarget)
      : null

    const payload = {
      kind: "group",
      status: this.groupStatusValue,
      // Groups are stored upper-cased, as the React drawer sends them.
      name: values.name.toUpperCase(),
      segment_id: values.segment_id || null,
      business_industry_id: subIndustry || industry || null,
      currencies: values.currency ? [values.currency] : [],
      revenue: this._toFloat(values.revenue),
      employees_number: this._toInt(values.employees_number),
      sites_number: this._toInt(values.sites_number),
      operating_countries_number: this._toInt(
        values.operating_countries_number,
      ),
      // Stored as whole numbers; the React drawer scales both the same way.
      three_years_growth_rate: this._toPercent(values.three_years_growth_rate),
      ebit_percentage: this._toPercent(values.ebit_percentage),
    }

    if (button) button.disabled = true
    try {
      const response = await post(this.groupUrlValue, {
        body: { contact_company: payload },
        contentType: "application/json",
        responseKind: "json",
        credentials: "include",
      })
      const json = await this._json(response)
      const created = json?.id ? json : json?.contact_company

      if (!response.ok || !created?.id) {
        // The server's `error`/`message` is an untranslated developer
        // diagnostic, so the box shows its own translated default instead.
        this._showGroupError()
        return
      }

      this._selectGroup(created.id, created.name)
      this._hideGroupError()
      this._clearFields(this.groupFieldsTarget)
      this._closeDrawerContaining(this.groupFieldsTarget)
    } catch {
      this._showGroupError()
    } finally {
      if (button) button.disabled = false
    }
  }

  // --- Dependent selects ---------------------------------------------------

  // The subcategory field only shows once the chosen category has children,
  // as in the React drawer (CONEK-8934).
  groupCategoryChanged() {
    if (!this.hasGroupCategoryTarget || !this.hasGroupSubcategoryTarget) return
    const children = this._filterDependent(
      this.groupCategoryTarget,
      this.groupSubcategoryTarget,
      this.industryParentsValue,
    )
    if (this.hasGroupSubcategoryFieldTarget) {
      this.groupSubcategoryFieldTarget.classList.toggle(
        "hidden",
        children.length === 0,
      )
    }
  }

  // CONEK-6480: the chosen segment narrows the industry selects to the allowed
  // combinations — the same rule the backend enforces on save, and the same
  // filter the React drawer applies through the business_industries API.

  groupSegmentChanged() {
    if (!this.hasGroupSegmentTarget) return
    // Financial data is End User only, as in the React drawer (CONEK-9120):
    // hidden with no type chosen and for the Non-End User segment.
    if (this.hasGroupFinancialFieldsTarget) {
      const segment = this._selectValue(this.groupSegmentTarget)
      this.groupFinancialFieldsTarget.hidden =
        !segment || segment === this.nonEndUserSegmentValue
    }
    if (!this.hasGroupCategoryTarget) return
    this._filterBySegment(this.groupSegmentTarget, this.groupCategoryTarget)
  }

  // --- Submit --------------------------------------------------------------

  created(event) {
    if (event?.detail?.success === false) return
    if (this.hasErrorsTarget) this.errorsTarget.replaceChildren()
    if (this._selectMode) {
      this._selectCreatedSite(event)
      this._resetDrawerAfterClose()
    }
    this._closeDrawerContaining(this.hiddenInputsTarget)
  }

  streamRendering(event) {
    const target = document.getElementById(
      event.target?.getAttribute?.("target") || "",
    )
    if (!target || !this.element.contains(target)) return

    const render = event.detail.render
    event.detail.render = async (stream) => {
      await render(stream)
      this._revealFirstError()
    }
  }

  _revealFirstError() {
    const error = Array.from(
      this.element.querySelectorAll('form [role="alert"]'),
    ).find((element) => element.textContent.trim() !== "")
    ;(error?.closest('[id$="-field"]') ?? error)?.scrollIntoView({
      block: "center",
    })
  }

  // Edit mode = the drawer was told which entity selector to fill.
  get _selectMode() {
    return this.hasSelectIntoValue && Boolean(this.selectIntoValue)
  }

  // Edit mode: the endpoint put the new site in response headers (a body would
  // make Turbo render it). Commit it through the selector's own write API so the
  // display, aria state, clear button and change events all follow.
  //
  // Both misses below are programming errors (an endpoint or markup change), not
  // user errors: the site exists, so failing silently would leave it created and
  // never selected. Loud in the console, which the system tests also capture.
  _selectCreatedSite(event) {
    const headers = event?.detail?.fetchResponse?.response?.headers
    const id = headers?.get?.("X-Created-Site-Id")
    if (!id) {
      console.error(
        "[company-creation] the create response carries no X-Created-Site-Id header; the Site field was not updated",
      )
      return
    }
    const name = decodeURIComponent(headers.get("X-Created-Site-Name") || "")

    const selectorEl = document.getElementById(this.selectIntoValue)
    if (!selectorEl) {
      console.error(
        `[company-creation] no element with id "${this.selectIntoValue}" to select site ${id} into`,
      )
      return
    }

    // CONEK-6667a — the Site field is an entity selector with the multi-company flag
    // off and a multi-select with it on, and the drawer is shared by both. Dispatch on
    // whichever controller the element actually carries rather than assuming.
    const single = this.application.getControllerForElementAndIdentifier(
      selectorEl,
      "ui--entity-selector",
    )
    if (single) {
      // The gem's write API — the same call the booking form's task drawer makes.
      // Guarded so a rename inside conek-ui reports itself here instead of throwing
      // mid-handler and leaving the drawer open on a site that already exists.
      if (typeof single._commitSelection !== "function") {
        console.error(
          "[company-creation] ui--entity-selector no longer exposes _commitSelection; the Site field was not updated",
        )
        return
      }
      single._commitSelection(id, name || id)
      return
    }

    const multi = this.application.getControllerForElementAndIdentifier(
      selectorEl,
      "ui--multi-select",
    )
    if (!multi) {
      console.error(
        `[company-creation] element "${this.selectIntoValue}" carries neither ui--entity-selector nor ui--multi-select; site ${id} was not selected`,
      )
      return
    }
    // `commitItem` is PUBLIC (conek-ui 0.1.32), unlike the entity selector's private
    // `_commitSelection` above — added precisely so this call does not depend on the
    // gem's internals.
    if (typeof multi.commitItem !== "function") {
      console.error(
        "[company-creation] ui--multi-select does not expose commitItem (needs conek_ui >= 0.1.32); the Site field was not updated",
      )
      return
    }
    const code = decodeURIComponent(headers.get("X-Created-Site-Code") || "")
    const address = decodeURIComponent(
      headers.get("X-Created-Site-Address") || "",
    )
    multi.commitItem({
      id,
      label: name || id,
      tags: code ? [code] : [],
      sublabel: address || undefined,
    })
  }

  // Edit mode only. On the Validate tab the redirect rebuilds the page after a
  // create; here the drawer survives, and a rep who clicked "+ Create" again
  // would find the previous site's name, group, owner and addresses still in it
  // — with nothing to stop a byte-identical second site (a company's name is not
  // unique). Rather than reset a dozen widget kinds one by one (Tom Select, the
  // entity selectors, tag inputs, sub-drawer rows), put the server-rendered
  // markup back once the drawer has slid shut (ui--drawer dispatches drawer:close
  // then): Stimulus reconnects every child controller on the fresh nodes, exactly
  // as on first render. Cancel deliberately does NOT reset — an accidental close
  // must not lose a half-filled form, same as the Validate tab.
  _resetDrawerAfterClose() {
    const drawerEl = this.hiddenInputsTarget.closest(
      '[data-controller~="ui--drawer"]',
    )
    if (!this._pristineHtml || !drawerEl) {
      // A silent return here would quietly bring the stale-drawer bug back.
      console.error(
        "[company-creation] cannot reset the New Site drawer after the create: " +
          (drawerEl
            ? "no pristine markup was captured at connect"
            : "the company form is not inside a ui--drawer"),
      )
      return
    }

    this._disarmDrawerClose()
    this._onDrawerClose = (event) => {
      // The sub-drawers close through the same bubbling event.
      if (event.target !== drawerEl) return
      this._restorePristine()
    }
    this.element.addEventListener("drawer:close", this._onDrawerClose)
  }

  // The one-shot close listener is dropped by whichever restore runs first —
  // the close itself, or turbo:before-cache while the drawer is still open.
  _disarmDrawerClose() {
    if (!this._onDrawerClose) return
    this.element.removeEventListener("drawer:close", this._onDrawerClose)
    this._onDrawerClose = null
  }

  // Back to the server-rendered drawer: closed, empty, every child controller
  // reconnected by Stimulus on the fresh nodes. Shared by the post-create reset
  // and the turbo:before-cache hook. Both callers reach this with the drawer
  // already closed and the body scroll lock already released: ui--drawer#close
  // does both before it dispatches drawer:close, and application.js's own
  // turbo:before-cache hook does both for every open drawer on that same event
  // (unconditionally, so it does not depend on listener order). The system test
  // asserts the released lock after the post-create close in a real browser.
  _restorePristine() {
    if (!this._pristineHtml) return
    this._disarmDrawerClose()
    this.element.innerHTML = this._pristineHtml
    this._socialMediaCount = 0
    this._addressCount = 0
    // The fresh selects need the same initial narrowing connect() applies.
    this.groupCategoryChanged()
    this.groupSegmentChanged()
    this._syncFirstAddressDefault()
  }

  // --- Internals -----------------------------------------------------------

  // Reads a sub-drawer's fields by their data-…-field name. Checkboxes report
  // "1"/"" so an unchecked box submits nothing rather than the string "false".
  //
  // The attribute is read with getAttribute, not dataset: the double hyphen in
  // the Stimulus identifier makes the dataset key
  // `contacts-CompanyCreationField`, which is a trap waiting to be mistyped.
  // The marked node is whatever the Ui component forwarded html_options onto —
  // sometimes the control, sometimes its wrapper — so resolve down to the
  // control before reading a value.
  _fieldNodes(scope) {
    return Array.from(
      scope.querySelectorAll("[data-contacts--company-creation-field]"),
    )
      .map((node) => ({
        key: node.getAttribute("data-contacts--company-creation-field"),
        control: this._control(node),
      }))
      .filter((entry) => entry.key && entry.control)
  }

  _control(node) {
    return node.matches("input, select, textarea")
      ? node
      : node.querySelector("input, select, textarea")
  }

  _readFields(scope) {
    const values = {}
    this._fieldNodes(scope).forEach(({ key, control }) => {
      values[key] =
        control.type === "checkbox"
          ? control.checked
            ? "1"
            : ""
          : (control.value ?? "")
    })
    return values
  }

  _clearFields(scope) {
    this._fieldNodes(scope).forEach(({ control }) => {
      if (control.type === "checkbox") control.checked = false
      else control.value = ""
      control.dispatchEvent(new Event("change", { bubbles: true }))
    })
  }

  // `path` is the bracket path between `contact_company[` and `][<index>]`, so a
  // nested collection passes e.g. "sites][0][shipping_addresses".
  _appendHidden(path, index, values, rowKey) {
    Object.entries(values).forEach(([key, value]) => {
      if (value === "" || value == null) return
      const input = document.createElement("input")
      input.type = "hidden"
      input.name = `contact_company[${path}][${index}][${key}]`
      input.value = value
      input.dataset.rowKey = rowKey
      this.hiddenInputsTarget.appendChild(input)
    })
  }

  _appendSummary(list, empty, text, rowKey) {
    const item = document.createElement("li")
    item.className =
      "flex items-center justify-between gap-3 rounded-md border border-border bg-muted/30 px-3 py-2 text-sm"
    item.dataset.rowKey = rowKey

    const label = document.createElement("span")
    label.className = "truncate"
    label.textContent = text
    item.appendChild(label)

    const remove = document.createElement("button")
    remove.type = "button"
    remove.className = "text-muted-foreground hover:text-danger shrink-0"
    remove.textContent = "×"
    remove.setAttribute("aria-label", "Remove")
    remove.addEventListener("click", () => this._removeRow(rowKey, list, empty))
    item.appendChild(remove)

    list.appendChild(item)
    empty.classList.add("hidden")
  }

  // Rows are removed by key, leaving gaps in the index sequence. Rails reads
  // `foo[0]`/`foo[2]` as two entries regardless of the gap, so there is nothing
  // to renumber.
  _removeRow(rowKey, list, empty) {
    list
      .querySelectorAll(`[data-row-key="${rowKey}"]`)
      .forEach((el) => el.remove())
    this.hiddenInputsTarget
      .querySelectorAll(`[data-row-key="${rowKey}"]`)
      .forEach((el) => el.remove())
    if (list.children.length === 0) empty.classList.remove("hidden")
    this._syncFirstAddressDefault()
  }

  _filterDependent(categoryEl, subcategoryEl, parents) {
    const category = this._selectValue(categoryEl)
    return this._narrow(
      subcategoryEl,
      (value) => Boolean(category) && parents?.[value] === category,
    )
  }

  // Hides the industry options not allowed for the chosen segment. With no
  // segment chosen every industry stays visible — the pair is only a pair once
  // both halves are picked, which is also when the backend validates it.
  _filterBySegment(segmentEl, industryEl) {
    const segment = this._selectValue(segmentEl)
    return this._narrow(industryEl, (value) =>
      this._segmentAllows(value, segment),
    )
  }

  // Narrows a select to the options `allows` keeps and returns them as
  // [label, value] pairs. Tom Select copies the <option>s once, when it is
  // built, and never re-reads them, so flagging the native options alone left
  // its control unchanged — every subcategory stayed greyed out (CONEK-8934).
  // The control gets the same list through the design system's public
  // `ui--select:set-options` door; the native flags keep the plain <select>
  // (and its value) in step. A single remaining option is selected for the
  // user: set-options disables a select with one option or none.
  _narrow(element, allows) {
    const select = this._nativeSelect(element)
    if (!select) return []

    const options = this._originalOptions(select).filter(([, value]) =>
      allows(value),
    )
    const allowed = new Set(options.map(([, value]) => value))
    const previous = select.value
    Array.from(select.options).forEach((option) => {
      if (!option.value) return
      option.hidden = !allowed.has(option.value)
      option.disabled = !allowed.has(option.value)
    })

    let value = allowed.has(previous) ? previous : ""
    if (!value && options.length === 1) value = options[0][1]
    select.value = value

    if (select.tomselect) {
      const host = select.closest('[data-controller~="ui--select"]')
      const setOptions = (detail) =>
        host?.dispatchEvent(
          new CustomEvent("ui--select:set-options", { detail }),
        )
      // set-options clears the options before the value, and Tom Select's
      // clearOptions keeps whichever option is still selected — so a chosen
      // subcategory survived into the next category's list. Deselect it with
      // an empty pass first.
      if (previous && !allowed.has(previous)) {
        setOptions({ options: [], value: null })
      }
      setOptions({ options, value: value || null })
    }
    select.dispatchEvent(new Event("change", { bubbles: true }))
    return options
  }

  // The full option list, captured the first time a select is narrowed —
  // connect() narrows both before Tom Select is built, so it is the server
  // render. Keyed by element: _restorePristine() swaps in fresh selects.
  _originalOptions(select) {
    this._optionSnapshots ??= new WeakMap()
    if (!this._optionSnapshots.has(select)) {
      this._optionSnapshots.set(
        select,
        Array.from(select.options)
          .filter((option) => option.value)
          .map((option) => [option.text, option.value]),
      )
    }
    return this._optionSnapshots.get(select)
  }

  _segmentAllows(industryId, segmentId) {
    if (!segmentId) return true
    const own = this.industrySegmentsValue?.[industryId]
    if (own?.length) return own.includes(segmentId)
    // A sub-industry with no declared pairs follows its parent's pairs — the
    // rule the CONEK-6480 catalogue backfill materialises server-side.
    const parent = this.industryParentsValue?.[industryId]
    const inherited = parent ? this.industrySegmentsValue?.[parent] : null
    return Boolean(inherited?.includes(segmentId))
  }

  _selectGroup(id, name) {
    if (!this.hasGroupSelectorTarget) return
    const hidden = this.groupSelectorTarget.querySelector(
      '[data-ui--entity-selector-target="hidden"]',
    )
    const display = this.groupSelectorTarget.querySelector(
      '[data-ui--entity-selector-target="display"]',
    )
    if (display) display.textContent = name ?? ""
    if (hidden) {
      hidden.value = id
      hidden.dispatchEvent(new Event("change", { bubbles: true }))
    }
  }

  _showGroupError() {
    if (!this.hasGroupErrorTarget) return
    this.groupErrorTarget.textContent =
      this.groupErrorTarget.dataset.defaultMessage || ""
    this.groupErrorTarget.classList.remove("hidden")
  }

  _hideGroupError() {
    if (!this.hasGroupErrorTarget) return
    this.groupErrorTarget.classList.add("hidden")
  }

  async _json(response) {
    try {
      return await response.json
    } catch {
      return null
    }
  }

  _closeDrawerContaining(element) {
    const drawerEl = element.closest('[data-controller~="ui--drawer"]')
    if (!drawerEl) return
    const controller = this.application.getControllerForElementAndIdentifier(
      drawerEl,
      "ui--drawer",
    )
    controller?.close()
  }

  _nativeSelect(element) {
    return element.tagName === "SELECT"
      ? element
      : element.querySelector("select")
  }

  _selectValue(element) {
    return this._nativeSelect(element)?.value || ""
  }

  _toInt(value) {
    return value === "" || value == null ? null : parseInt(value, 10)
  }

  _toFloat(value) {
    return value === "" || value == null ? null : parseFloat(value)
  }

  _toPercent(value) {
    const parsed = this._toFloat(value)
    return parsed == null ? null : parsed * 100
  }
}
